import { Component, computed, ElementRef, HostListener, input, OnInit, signal } from '@angular/core';
import { MiniChartsBase } from '../min-charts';

@Component({
  selector: 'chart-gauge',
  standalone: false,
  templateUrl: './gauge.component.html',
  styleUrls: ['./gauge.component.scss']
})
export class GaugeComponent extends MiniChartsBase implements OnInit {
  protected override get defaultWidth() { return 11; }
  protected override get defaultHeight() { return 9; }
  protected override get defaultSize() { return 20; }

  private readonly START_DEG = 180;
  private readonly SWEEP_DEG = 180;
  private readonly PADDING = 10;

  // Export mode - when true, disables auto-resize and uses fixed dimensions
  export = input<boolean>(false);

  // Signal to track container width for responsive sizing
  private containerWidth = signal<number>(0);

  constructor(private elementRef: ElementRef) {
    super();
  }

  ngOnInit(): void {
    // Skip initial size calculation in export mode
    if (!this.export()) {
      this.updateContainerWidth();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (!this.export()) {
      this.updateContainerWidth();
    }
  }

  private updateContainerWidth(): void {
    const container = this.elementRef.nativeElement;
    if (container && container.parentElement) {
      const parentWidth = container.parentElement.clientWidth;
      if (parentWidth > 0) {
        this.containerWidth.set(parentWidth);
      }
    }
  }

  // Override width to use container width when not in export mode
  // Max width is the original width input in rem * 16 (base rem to px)
  get responsiveWidth(): number {
    if (this.export()) {
      return this.widthInRem() * 16;
    }

    if (this.containerWidth() === 0) {
      return this.width();
    }
    const maxWidth = this.widthInRem() * 16;
    return Math.min(this.containerWidth(), maxWidth);
  }

  // Force calculate height based on width for optimal semi-circle gauge sizing
  get calculatedHeight(): number {
    // radius = (width - 2*PADDING - size) / 2, height = radius + size + 2*PADDING
    const r = (this.responsiveWidth - 2 * this.PADDING - this.size()) / 2;
    return r + this.size() + 2 * this.PADDING;
  }

  private get cx() { return this.responsiveWidth / 2; }
  private get cy() { return this.calculatedHeight - this.PADDING - this.size() / 2; }
  private get r() {
    // Calculate radius based on width (primary constraint for semi-circle)
    return Math.max(0, (this.responsiveWidth - 2 * this.PADDING - this.size()) / 2);
  }

  private getXYCord(cx: number, cy: number, r: number, deg: number) {
    const radian = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(radian), y: cy + r * Math.sin(radian) };
  }

  private createArc(sweepDeg: number = this.SWEEP_DEG): string {
    if (isNaN(sweepDeg) || isNaN(this.cx) || isNaN(this.cy) || isNaN(this.r)) {
      return '';
    }
    const start = this.getXYCord(this.cx, this.cy, this.r, this.START_DEG);
    const end = this.getXYCord(this.cx, this.cy, this.r, this.START_DEG + sweepDeg);
    const largeArcFlag = sweepDeg > 180 ? 1 : 0;
    const sweepFlag = 1;
    return `M ${start.x} ${start.y} A ${this.r} ${this.r} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`;
  }

  get trackPath(): string {
    return this.createArc();
  }

  get valuePath(): string {
    const val: any = this.value();
    if (isNaN(val)) return '';
    const pct = Math.max(0, Math.min(100, val));
    if (pct === 0) return '';
    const sweep = (this.SWEEP_DEG * pct) / 100;
    return this.createArc(sweep);
  }

  get textY(): number {
    const fontSize = Math.max(this.widthInRem(), this.size()) + 10;
    return this.cy - fontSize;
  }
}
